import React, { useEffect, useRef, useState } from 'react';
import { Divider } from '@moda/om';
import { logger } from '../../lib/logger';
import { tc } from '../../lib/trackingContext';
import { usePageType } from '../../hooks/usePageType';
import { useModuleTracking } from '../../hooks/useModuleTracking';

import './StyliticsWidget.scss';

interface Item {
  name: string;
}
interface Bundle {
  items: Item[];
  position: number;
}
interface BundlesData {
  length: number;
  bundles: Bundle[];
}
interface Props {
  variantId: string;
}

export const StyliticsWidget: React.FC<Props> = ({ variantId }) => {
  const [show, setShow] = useState(false);
  const pagetype = usePageType();
  const bundleDataRef = useRef<BundlesData | null>(null);
  const lastHoveredBundleIndex = useRef<number | null>(null);
  const lastClickedBundleIndex = useRef<number | null>(null);

  const { ref } = useModuleTracking({
    name: 'Stylitics',
    variantId: variantId
  });

  const chevronRightIcon =
    '<svg width="24px" height="24px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><polygon fill="#FFFFFF" fill-rule="nonzero" transform="translate(10.0379125, 10.0669412) scale(-1, 1) translate(-10.0379125, -10.0669412)" points="14.450825 3.008883 13.566942 2.125000 5.625000 10.066942 13.566942 18.008883 14.450825 17.125000 7.392767 10.066942"></polygon></g></svg>';

  const closeIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path style="fill:gray;" d="M12 11.323L19.323 4l.677.677L12.677 12 20 19.323l-.677.677L12 12.677 4.677 20 4 19.323 11.323 12 4 4.677 4.677 4 12 11.323z"/></svg>';

  const eyeIcon =
    '<svg width="20" height="20" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.66675 9.99999L3.75313 8.33089C7.40532 5.40914 12.5948 5.40914 16.247 8.33089L18.3334 9.99999" stroke="black" stroke-width="0.833333"/><path d="M1.66675 10L3.75313 11.6691C7.40532 14.5909 12.5951 14.5906 16.2473 11.6689C16.9784 11.0841 17.6914 10.5136 18.3334 10" stroke="black" stroke-width="0.833333" stroke-linecap="round"/><circle cx="10" cy="10" r="2.08333" stroke="#191A1B" stroke-width="0.833333" fill="none"/></svg>';

  const widgetInstance = useRef<StyliticsHotspotsWidget | null>(null);

  useEffect(() => {
    const widgetContainer = document.getElementById('stylitics-widget-container');
    if (widgetContainer && widgetContainer.children.length > 0) {
      return;
    }

    const styliticsScript = document.createElement('script');
    styliticsScript.src = 'https://web-assets.stylitics.com/v3-hotspots/latest/hotspots.release.js';
    styliticsScript.async = true;

    styliticsScript.onload = () => {
      if (typeof StyliticsHotspotsWidget !== 'undefined') {
        widgetInstance.current = new StyliticsHotspotsWidget(
          'modaoperandi',
          'stylitics-widget-container',
          {
            api: {
              item_number: variantId,
              max: 6,
              min: 1
            },
            display: {
              disableMnM: true,
              swipeableCarouselArrows: true,
              swipeableCarouselNextItemPeek: 0,
              hotspotsOverlayOrder: [['brand'], ['sales_price', 'price']]
            },
            text: {
              itemDetailsModalHeader: 'Styled By Moda'
            },
            price: {
              hideDoubleZeroCents: true,
              salesPriceStyle: 'strikethrough'
            }
          }
        );

        widgetInstance.current.on('mount', 'bundles', function (data: BundlesData) {
          if (data.bundles && data.bundles.length > 0) {
            setShow(true);
            bundleDataRef.current = data;
          }

          const bundleContainers = document.querySelectorAll('.stylitics-card');
          bundleContainers.forEach((container, index) => {
            container.addEventListener('mouseover', () => {
              if (lastHoveredBundleIndex.current !== index) {
                if (bundleDataRef.current) {
                  const hoveredBundleData = bundleDataRef.current.bundles[index];
                  if (hoveredBundleData) {
                    tc.track('Bundle Viewed', {
                      addToTrackingEvent: {
                        label: 'Stylitics',
                        items: hoveredBundleData.items
                      }
                    });
                    lastHoveredBundleIndex.current = index;
                  }
                }
              }
            });
          });
        });

        widgetInstance.current.on('mount', 'item', ({ element }: { element: HTMLElement }) => {
          // Update right arrow icons
          const svgElements = element.querySelectorAll('.stylitics-overlay-arrow-container');
          svgElements.forEach((el: Element) => {
            el.innerHTML = chevronRightIcon;
          });
          // Update close icon
          const itemList = document.querySelector('.stylitics-item-container');
          if (itemList !== null) {
            const closeContainer = document.querySelector('.stylitics-close');
            const svg = closeContainer?.querySelector('svg');
            if (svg) svg.innerHTML = closeIcon;
          }
        });

        // Update eye icon
        widgetInstance.current.on(
          'mount',
          'bundle',
          function ({ element }: { element: HTMLElement }) {
            const eyeIconContainer = element.querySelector('.stylitics-view-items-cta-icon');
            if (eyeIconContainer) {
              const svg = eyeIconContainer.querySelector('svg');
              if (svg) svg.innerHTML = eyeIcon;

              const span = document.createElement('span');
              span.innerText = 'VIEW LOOK';
              span.className = 'stylitics-view-look';
              eyeIconContainer.appendChild(span);

              span.addEventListener('click', () => {
                const bundleContainer = element.closest('.stylitics-bundle-container');

                if (bundleContainer) {
                  const bundleContainers = Array.from(
                    document.querySelectorAll('.stylitics-bundle-container')
                  );
                  const clickedIndex = bundleContainers.indexOf(bundleContainer);
                  lastClickedBundleIndex.current = clickedIndex;

                  if (bundleDataRef.current) {
                    const clickedBundleData = bundleDataRef.current.bundles[clickedIndex];
                    if (clickedBundleData) {
                      tc.track('Bundle Clicked', {
                        addToTrackingEvent: {
                          label: 'Stylitics',
                          bundleIndex: clickedBundleData.position,
                          items: clickedBundleData.items
                        }
                      });
                    }
                  }
                }

                const observer = new MutationObserver(mutations => {
                  mutations.forEach(mutation => {
                    if (mutation.addedNodes) {
                      mutation.addedNodes.forEach(addedNode => {
                        if (
                          addedNode.nodeType === Node.ELEMENT_NODE &&
                          (addedNode as Element).classList.contains('stylitics-next')
                        ) {
                          (addedNode as Element).addEventListener('click', () => {
                            tc.track('Bundle Page Clickthru', {
                              addToTrackingEvent: {
                                label: 'Stylitics',
                                previousBundleIndex: lastClickedBundleIndex.current
                              }
                            });
                          });
                        }
                      });
                    }
                  });
                });

                observer.observe(document.body, { childList: true, subtree: true });
              });
            }
          }
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        widgetInstance.current.on('click', 'item', function (data: any) {
          const isPopupVisible =
            document.querySelector('.stylitics-modal-inner') ||
            document.querySelector('.stylitics-desktop-modal-inner') ||
            document.querySelector('.stylitics-desktop-modal-main');

          !isPopupVisible &&
            tc.track('Bundle Item Clicked', {
              addToTrackingEvent: {
                label: 'Stylitics',
                item: data.item
              }
            });

          isPopupVisible &&
            tc.track('Popup Bundle Item Clicked', {
              addToTrackingEvent: {
                label: 'Stylitics',
                item: data.item
              }
            });
        });

        widgetInstance.current.start();
      }
    };

    styliticsScript.onerror = (message, source, lineno) => {
      logger.error('STYLITICS', 'Failed to load Stylitics script', {
        error: message,
        source: source,
        line: lineno
      });
    };

    document.body.appendChild(styliticsScript);
    return () => {
      document.body.removeChild(styliticsScript);
    };
  }, [pagetype, show, variantId]);

  useEffect(() => {
    if (widgetInstance.current) {
      widgetInstance.current.refresh({ api: { item_number: variantId } });
    }
  }, [variantId]);

  return (
    <div className="StyliticsHotspots" ref={ref}>
      {show && <Divider className="stylitics-header" text="Styled By Moda" />}
      <div id="stylitics-widget-container" />
    </div>
  );
};
